import React, { useState, useEffect } from 'react';
import { X, Users, Check } from 'lucide-react';
import { getTeams, addProjectToTeam, removeProjectFromTeam } from '../../services/teamService';
import type { Team } from '../../types/team';
import ConfirmDialog from '../ConfirmDialog';

interface ShareProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  projectName: string;
  currentTeamId?: string | null;
  onSuccess: () => void;
}

export const ShareProjectModal: React.FC<ShareProjectModalProps> = ({
  isOpen,
  onClose,
  projectId,
  projectName,
  currentTeamId,
  onSuccess,
}) => {
  const [teams, setTeams] = useState<Team[]>([]);
  const [selectedTeamId, setSelectedTeamId] = useState<string | null>(currentTeamId || null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showUnshareConfirm, setShowUnshareConfirm] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadTeams();
      setSelectedTeamId(currentTeamId || null);
    }
  }, [isOpen, currentTeamId]);

  const loadTeams = async () => {
    try {
      setIsLoading(true);
      const data = await getTeams();
      setTeams(data);
    } catch (err) {
      console.error('Error loading teams:', err);
      setError('チームの読み込みに失敗しました');
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!selectedTeamId && currentTeamId) {
      // チーム解除 - 確認ダイアログを表示
      setShowUnshareConfirm(true);
      return;
    } else if (selectedTeamId) {
      // チーム追加または変更
      setIsSubmitting(true);
      try {
        // 既存のチームから解除（必要な場合）
        if (currentTeamId && currentTeamId !== selectedTeamId) {
          await removeProjectFromTeam(projectId);
        }

        // 新しいチームに追加
        await addProjectToTeam(projectId, selectedTeamId);
        onSuccess();
        onClose();
      } catch (err) {
        console.error('Error sharing project:', err);
        const errorMessage = err instanceof Error ? err.message : 'プロジェクトの共有に失敗しました';

        // Handle duplicate key error
        if (errorMessage.includes('duplicate key') || errorMessage.includes('23505')) {
          setError('このプロジェクトは既にこのチームに共有されています');
        } else {
          setError(errorMessage);
        }
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleUnshareConfirm = async () => {
    setShowUnshareConfirm(false);
    setIsSubmitting(true);
    try {
      await removeProjectFromTeam(projectId);
      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error removing project from team:', err);
      setError(err instanceof Error ? err.message : 'チームからの解除に失敗しました');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) {
      setSelectedTeamId(null);
      setError(null);
      setShowUnshareConfirm(false);
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 animate-fade-slide-in">
      <div className="bg-white dark:bg-gray-800 rounded-xl max-w-md w-full shadow-2xl animate-scale-in">
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
              <Users className="w-5 h-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                プロジェクトを共有
              </h2>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                {projectName}
              </p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isSubmitting}
            className="p-2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors disabled:opacity-50 tap-highlight"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : teams.length === 0 ? (
            <div className="text-center py-8">
              <Users className="w-12 h-12 mx-auto mb-3 text-gray-300 dark:text-gray-600" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                チームがありません
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                先にチームを作成してください
              </p>
            </div>
          ) : (
            <>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                  共有先のチームを選択
                </label>
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {/* チーム解除オプション */}
                  {currentTeamId && (
                    <button
                      type="button"
                      onClick={() => setSelectedTeamId(null)}
                      className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                        selectedTeamId === null
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex items-center justify-center">
                            <X className="w-5 h-5 text-gray-500" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              共有を解除
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              個人プロジェクトに戻す
                            </p>
                          </div>
                        </div>
                        {selectedTeamId === null && (
                          <Check className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        )}
                      </div>
                    </button>
                  )}

                  {/* チーム一覧 */}
                  {teams.map((team) => (
                    <button
                      key={team.id}
                      type="button"
                      onClick={() => setSelectedTeamId(team.id)}
                      className={`w-full p-3 rounded-lg border-2 transition-all text-left ${
                        selectedTeamId === team.id
                          ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                          : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                      }`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                            <Users className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {team.name}
                            </p>
                            {team.description && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {team.description}
                              </p>
                            )}
                          </div>
                        </div>
                        {selectedTeamId === team.id && (
                          <Check className="w-5 h-5 text-blue-600 dark:text-blue-400" />
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleClose}
                  disabled={isSubmitting}
                  className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600
                    text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700
                    transition-colors disabled:opacity-50 disabled:cursor-not-allowed tap-highlight font-medium"
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting || (selectedTeamId === currentTeamId)}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg
                    hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed
                    flex items-center justify-center gap-2 tap-highlight font-medium"
                >
                  {isSubmitting ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      処理中...
                    </>
                  ) : selectedTeamId === null && currentTeamId ? (
                    '共有を解除'
                  ) : (
                    '共有'
                  )}
                </button>
              </div>
            </>
          )}
        </form>
      </div>

      <ConfirmDialog
        isOpen={showUnshareConfirm}
        title="チーム共有を解除"
        message="このプロジェクトをチームから解除しますか？\n\n注意: チーム解除により、すべてのタスクの担当者と進捗がクリアされます。"
        confirmText="解除する"
        cancelText="キャンセル"
        variant="warning"
        onConfirm={handleUnshareConfirm}
        onCancel={() => setShowUnshareConfirm(false)}
      />
    </div>
  );
};
